import { ApiError } from '../../lib/api.js';

interface Props {
  error: unknown;
  onRetry?: () => void;
}

export function ErrorState({ error, onRetry }: Props) {
  const { title, hint } = describe(error);
  return (
    <div
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center px-6 text-center"
    >
      <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-red-950/60 ring-1 ring-inset ring-red-900/60">
        <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-red-400" />
      </div>
      <div className="text-[13px] font-medium text-red-300">{title}</div>
      <div className="mt-1.5 max-w-md text-[12px] leading-relaxed text-zinc-500">{hint}</div>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-[12px] text-zinc-300 hover:border-zinc-700 hover:bg-zinc-800 hover:text-zinc-100"
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}

function describe(error: unknown): { title: string; hint: string } {
  if (error instanceof ApiError) {
    switch (error.kind) {
      case 'unauthorized':
        return {
          title: 'Session expired',
          hint: 'Your session expired — sign in again.',
        };
      case 'forbidden':
        return { title: 'Forbidden', hint: 'You do not have access to this.' };
      case 'not_found':
        return { title: 'Not found', hint: 'The resource does not exist.' };
      case 'rate_limited':
        return { title: 'Rate limited', hint: 'Too many requests. Try again shortly.' };
      case 'network':
        return {
          title: 'Server unreachable',
          hint: "Can't reach the server — retrying.",
        };
      case 'server':
        return { title: 'Server error', hint: `Upstream returned ${error.status}.` };
      default:
        return { title: 'Request failed', hint: `Unexpected status ${error.status}.` };
    }
  }
  const message = error instanceof Error ? error.message : String(error);
  return { title: 'Something went wrong', hint: message };
}
